import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import './App.css';
import Login from './Login';
import MaximusAdminDashboard from './MaximusAdminDashboard';
import SystemAdminDashboard from './SystemAdminDashboard';
import AdminDashboard from './AdminDashboard';
import ManagedUserView from './ManagedUserView';
import LocationsDashboard from './LocationsDashboard';
import GatesDashboard from './DeviceDashboard';
import RequestAccessLanding from './RequestAccessLanding';

// Main Application Component
function App() {
  return (
    <Routes>
      {/* Landing page for QR codes */}
      <Route path="/request-access" element={<RequestAccessLanding />} />
      
      {/* Admin Panel (default route) */}
      <Route path="/*" element={<AdminApp />} />
    </Routes>
  );
}

// The existing Admin Panel logic wrapped in a component
function AdminApp() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navContext, setNavContext] = useState([]);
  const [loginIntent, setLoginIntent] = useState({ asMaximus: false });
  const [loginError, setLoginError] = useState(null);
  const [appMode, setAppMode] = useState('NORMAL');

  const handleLogin = async (email, password, asMaximus) => {
    setLoginIntent({ asMaximus });
    setLoginError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error("Login failed", err);
      setLoginError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult(true);
        const userClaims = idTokenResult.claims;
        const trueRole = userClaims.role || { name: 'user', level: 1 };

        if (trueRole.level < 2) {
          setLoginError('Kun administratorer har tilgang.');
          auth.signOut();
          return;
        }
        
        setUser(user);
        setClaims(userClaims);

        if (trueRole.level === 4 && loginIntent.asMaximus) {
          setRole(trueRole);
          setAppMode('MAXIMUS_ISOLATED');
        } else if (trueRole.level === 4 && !loginIntent.asMaximus) {
          const downgradedRole = { name: 'systemAdmin', level: 3 };
          setRole(downgradedRole);
          setAppMode('NORMAL');
        } else {
          setRole(trueRole);
          setAppMode('NORMAL');
        }
        
        setNavContext([]);
      } else {
        setUser(null);
        setRole(null);
        setClaims(null);
        setAppMode('NORMAL');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [loginIntent]);

  const handleDrillDown = (context, type) => {
    setNavContext(prev => [...prev, { type, data: context }]);
  };

  const handleGoBack = () => {
    setNavContext(prev => prev.slice(0, -1));
  };

  const renderBreadcrumbs = () => (
    navContext.length > 0 && (
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => setNavContext([])}>Hjem</button>
        {navContext.map((item, index) => (
          <span key={index}>
            {' > '}
            <button onClick={() => setNavContext(prev => prev.slice(0, index + 1))}>
              {item.data.name || item.data.displayName || item.data.email}
            </button>
          </span>
        ))}
      </div>
    )
  );

  const renderDashboard = () => {
    const currentContext = navContext.length > 0 ? navContext[navContext.length - 1] : null;

    if (role?.level < 2) {
        return <p>Du har ikke tilgang til dette admin-panelet.</p>;
    }

    if (currentContext) {
        switch (currentContext.type) {
            case 'owner':
                return <LocationsDashboard owner={currentContext.data} onDrillDown={handleDrillDown} onBack={handleGoBack} />;
            case 'location':
                return <GatesDashboard location={currentContext.data} onDrillDown={handleDrillDown} onBack={handleGoBack} />;
            case 'user_L2':
                return <ManagedUserView admin={currentContext.data} onBack={handleGoBack} />;
            default:
                return <p>Ukjent visning.</p>;
        }
    }

    switch (role?.level) {
      case 4:
        return <MaximusAdminDashboard onDrillDown={(user) => handleDrillDown(user, 'user_L3')} />;
      case 3:
        return <SystemAdminDashboard onDrillDown={(context, type) => handleDrillDown(context, type)} />;
      case 2:
        return <AdminDashboard claims={claims} />;
      default:
        return <p>Du har ikke tilgang til dette admin-panelet.</p>;
    }
  };

  if (loading) {
    return <div>Laster inn...</div>;
  }

  if (!user) {
    return <Login onLogin={handleLogin} error={loginError} setError={setLoginError} />;
  }

  if (appMode === 'MAXIMUS_ISOLATED') {
    return (
      <div className="App">
        <header className="App-header">
          <h1>BifrostKey Admin (Maximus)</h1>
          <div>
            <span>{user.email} ({role ? role.name : ''})</span>
            <button onClick={() => auth.signOut()}>Logg ut</button>
          </div>
        </header>
        <main>
          <MaximusAdminDashboard />
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>BifrostKey Adminpanel</h1>
        <div>
          <span>{user.email} ({role ? role.name : ''})</span>
          <button onClick={() => auth.signOut()}>Logg ut</button>
        </div>
      </header>
      <main>
        {renderBreadcrumbs()}
        {renderDashboard()}
      </main>
    </div>
  );
}

export default App;
